/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { apiClient } from '../client'
import type {
  DocumentType,
  Center,
  DocumentStatus,
  InvoiceFilters,
  InvoicesResponse,
  AccountingDocument,
  UpdateInvoiceData,
} from '@/types/invoices.types'

/**
 * Načtení typů dokumentů
 */
export const getDocumentTypes = async (): Promise<DocumentType[]> => {
  const response = await apiClient.get<DocumentType[]>('/api/invoices/types')
  return response.data
}

/**
 * Načtení středisek
 */
export const getCenters = async (): Promise<Center[]> => {
  const response = await apiClient.get<Center[]>('/api/invoices/centers')
  return response.data
}

/**
 * Načtení statusů dokladů
 */
export const getDocumentStatuses = async (): Promise<DocumentStatus[]> => {
  const response = await apiClient.get<DocumentStatus[]>('/api/invoices/statuses')
  return response.data
}

/**
 * Nahrání dokumentu
 */
export const uploadDocument = async (
  file: File,
  centerId: string,
  typeCode: string
): Promise<void> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('centerId', centerId)
  formData.append('typeCode', typeCode)

  await apiClient.post('/api/invoices/documents', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/**
 * Načtení seznamu faktur s filtrováním, řazením a paginací
 */
export const getInvoices = async (filters?: InvoiceFilters): Promise<InvoicesResponse> => {
  const params = new URLSearchParams()

  if (filters?.page) params.append('page', filters.page.toString())
  if (filters?.perPage) params.append('perPage', filters.perPage.toString())
  if (filters?.centerId) params.append('centerId', filters.centerId)
  if (filters?.statusCode) params.append('statusCode', filters.statusCode)
  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom)
  if (filters?.dateTo) params.append('dateTo', filters.dateTo)
  if (filters?.dateDueFrom) params.append('dateDueFrom', filters.dateDueFrom)
  if (filters?.dateDueTo) params.append('dateDueTo', filters.dateDueTo)
  if (filters?.sortBy) params.append('sortBy', filters.sortBy)
  if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder)

  const response = await apiClient.get<InvoicesResponse>(
    `/api/invoices/documents?${params.toString()}`
  )
  return response.data
}

/**
 * Načtení detailu faktury
 */
export const getInvoice = async (id: number): Promise<AccountingDocument> => {
  const response = await apiClient.get<AccountingDocument>(`/api/invoices/documents/${id}`)
  return response.data
}

/**
 * Aktualizace faktury
 */
export const updateInvoice = async (
  id: number,
  data: UpdateInvoiceData
): Promise<AccountingDocument> => {
  const response = await apiClient.patch<AccountingDocument>(
    `/api/invoices/documents/${id}`,
    data
  )
  return response.data
}

/**
 * Smazání faktury (soft delete)
 */
export const deleteInvoice = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/invoices/documents/${id}`)
}

